/**
 * Frontend Logger Service
 *
 * Configurable logging system with log levels that can be controlled from settings.
 * Replaces direct console.log usage for better control and production readiness.
 *
 * Log Levels:
 * - DEBUG (0): Detailed debugging information
 * - INFO (1): General informational messages
 * - WARN (2): Warning messages
 * - ERROR (3): Error messages
 * - NONE (4): Disable all logging
 */

export const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

export const LogLevelNames = {
  0: 'DEBUG',
  1: 'INFO',
  2: 'WARN',
  3: 'ERROR',
  4: 'NONE'
};

class Logger {
  constructor() {
    this.storageKey = 'app_log_level';
    this.defaultLevel = process.env.NODE_ENV === 'production' ? LogLevel.ERROR : LogLevel.DEBUG;
  }

  /**
   * Get current log level from localStorage or use default
   * @returns {number} Current log level
   */
  getLogLevel() {
    const stored = localStorage.getItem(this.storageKey);
    if (stored !== null) {
      const level = parseInt(stored, 10);
      if (!isNaN(level) && level >= LogLevel.DEBUG && level <= LogLevel.NONE) {
        return level;
      }
    }
    return this.defaultLevel;
  }

  /**
   * Set log level and save to localStorage
   * @param {number} level - Log level to set
   */
  setLogLevel(level) {
    if (level >= LogLevel.DEBUG && level <= LogLevel.NONE) {
      localStorage.setItem(this.storageKey, level.toString());
    } else {
      throw new Error(`Invalid log level: ${level}`);
    }
  }

  /**
   * Check if a message should be logged based on current log level
   * @param {number} messageLevel - Level of the message to log
   * @returns {boolean} True if message should be logged
   */
  shouldLog(messageLevel) {
    return messageLevel >= this.getLogLevel();
  }

  /**
   * Format log message with timestamp and level
   * @param {string} level - Log level name
   * @param {Array} args - Arguments to log
   * @returns {Array} Formatted arguments
   */
  formatMessage(level, args) {
    const timestamp = new Date().toISOString();
    return [`[${timestamp}] [${level}]`, ...args];
  }

  /**
   * Log debug message
   * @param {...any} args - Arguments to log
   */
  debug(...args) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(...this.formatMessage('DEBUG', args));
    }
  }

  /**
   * Log info message
   * @param {...any} args - Arguments to log
   */
  info(...args) {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(...this.formatMessage('INFO', args));
    }
  }

  /**
   * Log warning message
   * @param {...any} args - Arguments to log
   */
  warn(...args) {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(...this.formatMessage('WARN', args));
    }
  }

  /**
   * Log error message
   * @param {...any} args - Arguments to log
   */
  error(...args) {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(...this.formatMessage('ERROR', args));
    }
  }

  /**
   * Log message with custom level
   * @param {number} level - Log level
   * @param {...any} args - Arguments to log
   */
  log(level, ...args) {
    if (this.shouldLog(level)) {
      const levelName = LogLevelNames[level] || 'LOG';
      const method = level >= LogLevel.ERROR ? console.error :
                     level >= LogLevel.WARN ? console.warn :
                     level >= LogLevel.INFO ? console.info :
                     console.log;
      method(...this.formatMessage(levelName, args));
    }
  }
}

// Create and export singleton instance
const logger = new Logger();
export default logger;
